import { useCallback, useRef, useEffect } from 'react';
import websocketService from '../../../../../services/websocketService';
import './devHelpers';
import { WebSocketScope } from '../../../../../utils/enums';
import { DeltaBuffer } from '../../services';
import { MessageEvent, MessageStatus } from '../../constants/streamingConstants';

const CONVERSATION_SCOPE = `scope:${WebSocketScope.CONVERSATION}`;

const EVENT_LOWER = Object.fromEntries(
  Object.entries(MessageEvent).map(([k, v]) => [k, v.toLowerCase()])
);

/** Monotonic counter for stream IDs in normalizePayload (module-level, no hook) */
let streamIdCounter = 0;

/** Same conversation (compare as strings to avoid type mismatch) */
function isSameConversation(currentId, payloadId) {
  if (!currentId) return true;
  if (!payloadId) return false;
  return String(currentId) === String(payloadId);
}

/**
 * Normalize payload: handle both payload.context + payload.data (new) and flat payload (legacy).
 * For legacy format (flat payload without payload.data), do NOT use data.content fallback - it echoes user's question.
 */
function normalizePayload(message) {
  const raw = message?.payload ?? message?.data ?? message ?? {};
  const context = raw.context ?? {};
  const hasExplicitData = !!raw.data && raw.data !== raw;
  const data = hasExplicitData ? raw.data : raw;

  const conversationUuid =
    context.conversation_id ??
    context.conversationId ??
    raw.conversationUuid ??
    raw.conversation_uuid ??
    raw.conversationId;

  // Use || so empty string falls through (backend often sends assistant_message_id: "" or null)
  const messageId =
    context.assistant_message_id ||
    context.response_id ||
    message?.ref_id ||
    data.messageId ||
    data.message_id ||
    data.message_uuid ||
    raw.messageId ||
    raw.message_id ||
    'stream-' + (++streamIdCounter);

  const chunkContent =
    data.text ??
    data.chunkContent ??
    data.chunk_content ??
    (hasExplicitData ? data.content : undefined);

  const deltaIndex = data.index ?? data.chunkIndex ?? data.chunk_index;
  const sequenceId = message?.sequence;

  return {
    context,
    data,
    hasExplicitData,
    conversationUuid,
    messageId,
    chunkContent,
    deltaIndex,
    sequenceId,
  };
}

/**
 * WebSocket subscription for chat streaming.
 * Handles 25+ event types from backend transport envelope.
 * Uses DeltaBuffer for ordered delta flushing.
 */
export const useHomeChatStreaming = (options = {}) => {
  const {
    conversationId,
    setMessages,
    messagesRef,
    setAgentIsTyping,
    setCurrentActivity,
    setToolCalls,
    setHitlRequest,
    setSources,
    setUiBlocks,
    setMessageStatus,
    setPlanSteps,
    setIsStreaming,
  } = options;

  const bufferRef = useRef(null);
  const currentMessageIdRef = useRef(null);
  const usedMessageIdsRef = useRef(new Set());
  const messageIdCounterRef = useRef(0);
  const completedReceivedRef = useRef(false);
  const graceTimerRef = useRef(null);
  const lastDeltaTimerRef = useRef(null);
  const prevConversationIdRef = useRef(conversationId);
  const activeConversationIdRef = useRef(conversationId);

  /** Fallback: clear typing if no new delta for 2s (backend may not send completion events) */
  const NO_DELTA_TYPING_STOP_MS = 2000;

  const refs = useRef({
    setMessages,
    setAgentIsTyping,
    setCurrentActivity,
    setToolCalls,
    setHitlRequest,
    setSources,
    setUiBlocks,
    setMessageStatus,
    setPlanSteps,
    setIsStreaming,
  });
  useEffect(() => {
    refs.current = {
      setMessages,
      setAgentIsTyping,
      setCurrentActivity,
      setToolCalls,
      setHitlRequest,
      setSources,
      setUiBlocks,
      setMessageStatus,
      setPlanSteps,
      setIsStreaming,
    };
  }, [
    setMessages,
    setAgentIsTyping,
    setCurrentActivity,
    setToolCalls,
    setHitlRequest,
    setSources,
    setUiBlocks,
    setMessageStatus,
    setPlanSteps,
    setIsStreaming,
  ]);

  const updateMessageContent = useCallback(
    (messageId, content) => {
      const setter = refs.current.setMessages;
      if (!setter) return;
      setter((prev) => {
        const next = [...prev];
        const idx = next.findIndex((m) => m.id === messageId || m.messageUuid === messageId);
        const base = idx >= 0 ? next[idx] : {};
        const msg = {
          id: messageId,
          uuid: messageId,
          messageUuid: messageId,
          content,
          role: 'assistant',
          createdAt: base.createdAt ?? new Date().toISOString(),
          timestamp: base.timestamp ?? new Date().toISOString(),
          conversationId: activeConversationIdRef.current ?? base.conversationId ?? null,
          attachments: base.attachments ?? [],
          metadata: base.metadata ?? {},
        };
        if (idx >= 0) {
          next[idx] = { ...base, ...msg };
        } else {
          next.push(msg);
        }
        return next;
      });
    },
    []
  );

  const ensureBuffer = useCallback(
    (messageId) => {
      if (currentMessageIdRef.current !== messageId) {
        if (bufferRef.current) {
          bufferRef.current.destroy();
          bufferRef.current = null;
        }
        if (graceTimerRef.current) {
          clearTimeout(graceTimerRef.current);
          graceTimerRef.current = null;
        }
        completedReceivedRef.current = false;
        currentMessageIdRef.current = messageId;
        refs.current.setMessageStatus?.(null);
      }
      if (!bufferRef.current) {
        bufferRef.current = new DeltaBuffer({
          onFlush: (text) => updateMessageContent(messageId, text),
          inactivityTimeout: 30000,
          graceMs: 3000,
        });
      }
      return bufferRef.current;
    },
    [updateMessageContent]
  );

  const resetStreamingState = useCallback(() => {
    if (lastDeltaTimerRef.current) {
      clearTimeout(lastDeltaTimerRef.current);
      lastDeltaTimerRef.current = null;
    }
    if (bufferRef.current) {
      bufferRef.current.destroy();
      bufferRef.current = null;
    }
    if (currentMessageIdRef.current) {
      usedMessageIdsRef.current.add(currentMessageIdRef.current);
    }
    currentMessageIdRef.current = null;
    completedReceivedRef.current = false;
    refs.current.setAgentIsTyping?.(false);
    refs.current.setIsStreaming?.(false);
    refs.current.setCurrentActivity?.(null);
    refs.current.setToolCalls?.([]);
    refs.current.setMessageStatus?.(null);
    refs.current.setPlanSteps?.([]);
    refs.current.setSources?.([]);
  }, []);

  const scheduleTypingStopFallback = useCallback(() => {
    if (lastDeltaTimerRef.current) clearTimeout(lastDeltaTimerRef.current);
    lastDeltaTimerRef.current = setTimeout(() => {
      lastDeltaTimerRef.current = null;
      if (!completedReceivedRef.current) {
        refs.current.setAgentIsTyping?.(false);
        refs.current.setIsStreaming?.(false);
      }
    }, NO_DELTA_TYPING_STOP_MS);
  }, []);

  useEffect(() => {
    activeConversationIdRef.current = conversationId;
    const prev = prevConversationIdRef.current;
    prevConversationIdRef.current = conversationId;
    if (bufferRef.current) {
      bufferRef.current.destroy();
      bufferRef.current = null;
    }
    currentMessageIdRef.current = null;
    usedMessageIdsRef.current.clear();
    completedReceivedRef.current = false;
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
    if (lastDeltaTimerRef.current) {
      clearTimeout(lastDeltaTimerRef.current);
      lastDeltaTimerRef.current = null;
    }
    if (prev && conversationId && prev !== conversationId) {
      resetStreamingState();
    }
  }, [conversationId, resetStreamingState]);

  const resolveMessageId = useCallback(
    (rawMessageId, convUuid) => {
      if (currentMessageIdRef.current) return currentMessageIdRef.current;

      const existsInHistory = rawMessageId && (messagesRef?.current || []).some(
        (m) => m.id === rawMessageId || m.messageUuid === rawMessageId
      );

      if (
        rawMessageId &&
        rawMessageId !== convUuid &&
        rawMessageId !== activeConversationIdRef.current &&
        !usedMessageIdsRef.current.has(rawMessageId) &&
        !existsInHistory
      ) {
        return rawMessageId;
      }
      const id = ++messageIdCounterRef.current;
      return `assistant-${id}-${Math.random().toString(36).slice(2, 8)}`;
    },
    [messagesRef]
  );

  const processDelta = useCallback(
    (message) => {
      const { conversationUuid, messageId: rawMsgId, chunkContent, deltaIndex, sequenceId } =
        normalizePayload(message);

      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      if ((chunkContent ?? '') === '' || !rawMsgId) return;

      const isConvIdAsMessageId =
        rawMsgId === conversationUuid || rawMsgId === activeConversationIdRef.current;
      const hasFlushed =
        currentMessageIdRef.current &&
        bufferRef.current &&
        bufferRef.current.flushedContent.length > 0;
      const shouldStartNew = deltaIndex === 0 && hasFlushed;

      if (shouldStartNew) {
        usedMessageIdsRef.current.add(currentMessageIdRef.current);
        bufferRef.current.destroy();
        bufferRef.current = null;
        currentMessageIdRef.current = null;
      }

      const messageId = (shouldStartNew || isConvIdAsMessageId)
        ? resolveMessageId(null, conversationUuid)
        : resolveMessageId(rawMsgId, conversationUuid);

      refs.current.setAgentIsTyping?.(true);
      refs.current.setIsStreaming?.(true);

      const buffer = ensureBuffer(messageId);
      const effectiveIndex = deltaIndex ?? sequenceId ?? buffer.getNextAppendIndex();
      buffer.push(
        typeof effectiveIndex === 'number' ? effectiveIndex : buffer.getNextAppendIndex(),
        chunkContent,
        sequenceId
      );
      scheduleTypingStopFallback();
    },
    [ensureBuffer, scheduleTypingStopFallback, resolveMessageId]
  );

  const handleOutputTextDelta = processDelta;
  const handleLegacyDelta = processDelta;

  const handleCompleted = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);

      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;

      completedReceivedRef.current = true;
      const buffer = bufferRef.current;
      if (buffer) {
        buffer.markFin(null);
        graceTimerRef.current = setTimeout(() => {
          graceTimerRef.current = null;
          resetStreamingState();
        }, 3000);
      } else {
        resetStreamingState();
      }
    },
    [resetStreamingState]
  );

  const handleOutputItemDone = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      refs.current.setAgentIsTyping?.(false);
    },
    []
  );

  const handleFinalizing = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      refs.current.setAgentIsTyping?.(false);
    },
    []
  );

  const handleFailed = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      refs.current.setMessageStatus?.(MessageStatus.FAILED);
      resetStreamingState();
    },
    [resetStreamingState]
  );

  const handleIncomplete = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      refs.current.setMessageStatus?.(MessageStatus.INCOMPLETE);
      resetStreamingState();
    },
    [resetStreamingState]
  );

  const handleCancelled = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      refs.current.setMessageStatus?.(MessageStatus.CANCELLED);
      resetStreamingState();
    },
    [resetStreamingState]
  );

  const handleActivityStarted = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const data = payload.data ?? payload;
      refs.current.setCurrentActivity?.({
        label: data.label ?? data.description ?? 'Processing...',
        kind: data.kind ?? 'default',
      });
    },
    []
  );

  const handleActivityDone = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      refs.current.setCurrentActivity?.(null);
    },
    []
  );

  const handleToolCallStarted = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const data = payload.data ?? payload;
      refs.current.setToolCalls?.((prev) => [
        ...prev,
        {
          id: data.id ?? data.tool_call_id ?? Date.now(),
          name: data.name ?? data.tool_name ?? 'Tool',
          status: 'started',
          startedAt: new Date().toISOString(),
        },
      ]);
    },
    []
  );

  const handleToolCallCompleted = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const data = payload.data ?? payload;
      const id = data.id ?? data.tool_call_id;
      refs.current.setToolCalls?.((prev) =>
        prev.map((tc) =>
          tc.id === id
            ? { ...tc, status: 'completed', completedAt: new Date().toISOString() }
            : tc
        )
      );
    },
    []
  );

  const handleToolCallFailed = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const data = payload.data ?? payload;
      const id = data.id ?? data.tool_call_id;
      refs.current.setToolCalls?.((prev) =>
        prev.map((tc) =>
          tc.id === id ? { ...tc, status: 'failed', completedAt: new Date().toISOString() } : tc
        )
      );
    },
    []
  );

  const handlePlanPublished = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const steps = payload.data?.steps ?? payload.steps ?? [];
      refs.current.setPlanSteps?.(steps);
    },
    []
  );

  const handlePlanStepUpdated = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const step = payload.data ?? payload;
      refs.current.setPlanSteps?.((prev) =>
        prev.map((s) => (s.id === step.id ? { ...s, ...step } : s))
      );
    },
    []
  );

  const handleSourcesFinal = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      const sourcesList = payload.data?.sources ?? payload.sources ?? [];
      refs.current.setSources?.(sourcesList);
    },
    []
  );

  const handleInputRequired = useCallback(
    (message) => {
      const { conversationUuid } = normalizePayload(message);
      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;
      const payload = message?.payload ?? message?.data ?? {};
      refs.current.setHitlRequest?.(payload.data ?? payload);
    },
    []
  );

  const handleLegacyComplete = useCallback(
    (message) => {
      const { conversationUuid, chunkContent, deltaIndex } = normalizePayload(message);

      if (!isSameConversation(activeConversationIdRef.current, conversationUuid)) return;

      const buffer = bufferRef.current;
      if (!buffer) return;

      if (chunkContent) {
        const effectiveIndex = typeof deltaIndex === 'number' ? deltaIndex : buffer.getNextAppendIndex();
        buffer.push(effectiveIndex, chunkContent, message?.sequence);
      }
      buffer.markFin(null);
      completedReceivedRef.current = true;
      graceTimerRef.current = setTimeout(() => {
        graceTimerRef.current = null;
        resetStreamingState();
      }, 3000);
    },
    [resetStreamingState]
  );

  const handleConversationMessage = useCallback(
    (message) => {
      const event = String(message?.event ?? message?.type ?? '').toLowerCase();

      if (event === EVENT_LOWER.OUTPUT_TEXT_DELTA) return handleOutputTextDelta(message);
      if (event === EVENT_LOWER.RESPONSE_COMPLETED) return handleCompleted(message);
      if (event === EVENT_LOWER.OUTPUT_ITEM_DONE) return handleOutputItemDone(message);
      if (event === EVENT_LOWER.RESPONSE_FINALIZING) return handleFinalizing(message);
      if (event === EVENT_LOWER.RESPONSE_FAILED) return handleFailed(message);
      if (event === EVENT_LOWER.RESPONSE_INCOMPLETE) return handleIncomplete(message);
      if (event === EVENT_LOWER.RESPONSE_CANCELLED || event === EVENT_LOWER.RESPONSE_CANCEL_ACK) return handleCancelled(message);
      if (event === EVENT_LOWER.ACTIVITY_STARTED) return handleActivityStarted(message);
      if (event === EVENT_LOWER.ACTIVITY_DONE) return handleActivityDone(message);
      if (event === EVENT_LOWER.TOOL_CALL_STARTED) return handleToolCallStarted(message);
      if (event === EVENT_LOWER.TOOL_CALL_COMPLETED) return handleToolCallCompleted(message);
      if (event === EVENT_LOWER.TOOL_CALL_FAILED) return handleToolCallFailed(message);
      if (event === EVENT_LOWER.PLAN_PUBLISHED) return handlePlanPublished(message);
      if (event === EVENT_LOWER.PLAN_STEP_UPDATED) return handlePlanStepUpdated(message);
      if (event === EVENT_LOWER.SOURCES_FINAL) return handleSourcesFinal(message);
      if (event === EVENT_LOWER.INPUT_REQUIRED) return handleInputRequired(message);
      if (event === 'delta') return handleLegacyDelta(message);
      if (event === 'complete') return handleLegacyComplete(message);
    },
    [
      handleOutputTextDelta,
      handleCompleted,
      handleOutputItemDone,
      handleFinalizing,
      handleFailed,
      handleIncomplete,
      handleCancelled,
      handleActivityStarted,
      handleActivityDone,
      handleToolCallStarted,
      handleToolCallCompleted,
      handleToolCallFailed,
      handlePlanPublished,
      handlePlanStepUpdated,
      handleSourcesFinal,
      handleInputRequired,
      handleLegacyDelta,
      handleLegacyComplete,
    ]
  );

  useEffect(() => {
    websocketService.on(CONVERSATION_SCOPE, handleConversationMessage);
    return () => {
      websocketService.off(CONVERSATION_SCOPE, handleConversationMessage);
      if (bufferRef.current) {
        bufferRef.current.destroy();
        bufferRef.current = null;
      }
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
      }
      if (lastDeltaTimerRef.current) {
        clearTimeout(lastDeltaTimerRef.current);
        lastDeltaTimerRef.current = null;
      }
    };
  }, [handleConversationMessage]);

  return {};
};
